import {
  Form as FormDS,
  FormItemDecorated as FormItemDecoratedDS,
  FormItem as FormItemDS,
  FormItemProps as FormItemPropsDS,
  FormProps as FormPropsDS,
} from '@billy/design-system'
import { yupResolver } from '@hookform/resolvers/yup'
import { zodResolver } from '@hookform/resolvers/zod'
import isEqual from 'lodash/isEqual'
import {
  forwardRef,
  ForwardRefExoticComponent,
  ReactElement,
  ReactNode,
  Ref,
  RefAttributes,
  useMemo,
  useRef,
} from 'react'
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  CriteriaMode,
  DefaultValues,
  FormProvider,
  Mode,
  Path,
  PathValue,
  UnpackNestedValue,
  useFieldArray as useFieldArrayForm,
  useFormContext as useFormContextForm,
  UseFormReturn as UseFormReturnForm,
  UseFormStateReturn,
  useForm as useHookForm,
  useWatch as useWatchForm,
} from 'react-hook-form'
import { Asserts as YupAsserts, AnyObjectSchema as YupSchema } from 'yup'
import { z, ZodSchema } from 'zod'

import { DesignSystem } from '../../enums/designSystem'
import { fixDefaultValues } from './utils/fixDefaultValues'
import { reset } from './utils/reset'
import { setValue } from './utils/setValue'

type SupportedSchema = YupSchema | ZodSchema
type AssertYupSchema<T> = T extends YupSchema ? YupAsserts<T> : never
type AssertZodSchema<T> = T extends ZodSchema ? z.infer<T> : never
type SchemaAsserts<T> = AssertYupSchema<T> | AssertZodSchema<T>

export type UseFormParams<T extends SupportedSchema, D extends DesignSystem = DesignSystem.Billy> = {
  criteriaMode?: CriteriaMode
  defaultValues?: DefaultValues<SchemaAsserts<T>>
  delayError?: number
  // Picks which design system's fields `FormItem` wraps. Defaults to Billy.
  designSystem?: D
  mode?: Mode
  onSubmit?: (values: SchemaAsserts<T>) => any
  onSubmitError?: (error: unknown, setError: UseFormReturnForm<SchemaAsserts<T>>['setError']) => void
  reValidateMode?: 'onBlur' | 'onChange' | 'onSubmit'
  validationSchema: T // this can be either a Yup or Zod schema
}

export type FormComponent = ForwardRefExoticComponent<FormPropsDS & RefAttributes<HTMLFormElement>>
export type FormControl<T extends SupportedSchema> = Control<SchemaAsserts<T>>
export type UseFormReturn<T extends SupportedSchema, D extends DesignSystem = DesignSystem.Billy> = ReturnType<
  typeof useForm<T, D>
>

type FormItemComponent<T extends SupportedSchema, D extends DesignSystem> = D extends DesignSystem.Lumen
  ? <Name extends Path<SchemaAsserts<T>>>(props: Omit<FormItemLumenProps<T, Name>, 'control'>) => ReactElement
  : <Name extends Path<SchemaAsserts<T>>>(props: Omit<FormItemProps<T, Name>, 'error'>) => ReactElement

type UseFormContextOptions<D extends DesignSystem> = {
  designSystem: D
}

// Only optional for Billy, so a Lumen generic can't be passed without the matching runtime value.
type UseFormContextArgs<D extends DesignSystem> = D extends DesignSystem.Billy
  ? [options?: UseFormContextOptions<D>]
  : [options: UseFormContextOptions<D>]

const isYupSchema = (schema: unknown): schema is YupSchema => {
  return typeof (schema as YupSchema)?.validateSync === 'function'
}

const isZodSchema = (schema: unknown): schema is ZodSchema => {
  return typeof (schema as ZodSchema)?.safeParse === 'function'
}

export const useForm = <T extends SupportedSchema, D extends DesignSystem = DesignSystem.Billy>(
  params: UseFormParams<T, D>,
) => {
  const designSystem = params.designSystem ?? DesignSystem.Billy
  const hookForm = useHookForm<SchemaAsserts<T>>({
    criteriaMode: params.criteriaMode,
    defaultValues: params.defaultValues,
    delayError: params.delayError,
    mode: params.mode,
    resolver: useMemo(() => {
      let resolver

      if (isYupSchema(params.validationSchema)) {
        resolver = yupResolver(params.validationSchema)
      }
      if (isZodSchema(params.validationSchema)) {
        resolver = zodResolver(params.validationSchema)
      }

      if (!resolver) {
        throw new Error('Unsupported schema type')
      }

      return resolver
    }, [params.validationSchema]),
    reValidateMode: params.reValidateMode || 'onChange',
  })

  fixDefaultValues(hookForm)

  const hookFormRef = useRef(hookForm)
  const paramsRef = useRef(params)

  hookFormRef.current = hookForm
  paramsRef.current = params

  const submitHandler = useMemo(
    () =>
      hookFormRef.current.handleSubmit((values: SchemaAsserts<T>) => {
        try {
          paramsRef.current.onSubmit?.(values)
        } catch (e) {
          paramsRef.current.onSubmitError?.(e, hookFormRef.current.setError)
        }
      }),
    [],
  )

  return {
    ...hookForm,
    submitHandler,
    setValue: useMemo(() => setValue(hookForm), [hookForm]),
    reset: useMemo(() => reset(hookForm), [hookForm]),
    Form: useMemo(() => {
      // eslint-disable-next-line prefer-arrow-callback -- a named function keeps the component name in React DevTools
      return forwardRef(function UseForm(props: FormPropsDS, forwardedRef: Ref<HTMLFormElement>) {
        return (
          <FormProvider {...hookFormRef.current}>
            <FormDS
              noValidate
              ref={forwardedRef}
              {...props}
              onSubmit={(e) => {
                e.stopPropagation()
                submitHandler(e)
              }}
            />
          </FormProvider>
        )
      })
    }, [submitHandler]),
    FormItem: useMemo(() => {
      if (designSystem === DesignSystem.Lumen) {
        return function UseFormItemLumen<Name extends Path<SchemaAsserts<T>>>(
          props: Omit<FormItemLumenProps<T, Name>, 'control'>,
        ) {
          return <FormItemLumenComponent {...props} control={hookForm.control} />
        }
      }

      return function UseFormItem<Name extends Path<SchemaAsserts<T>>>(props: Omit<FormItemProps<T, Name>, 'error'>) {
        return <FormItemDecoratedComponent {...props} control={hookForm.control} />
      }
    }, [designSystem, hookForm.control]) as FormItemComponent<T, D>,
  }
}

export const useFormContext = <T extends SupportedSchema, D extends DesignSystem = DesignSystem.Billy>(
  ...[options]: UseFormContextArgs<D>
) => {
  const formContext = useFormContextForm<SchemaAsserts<T>>()
  const designSystem = options?.designSystem ?? DesignSystem.Billy

  return {
    ...formContext,
    setValue: useMemo(() => setValue(formContext), [formContext]),
    reset: useMemo(() => reset(formContext), [formContext]),
    FormItem: useMemo(() => {
      if (designSystem === DesignSystem.Lumen) {
        return function UseFormItemLumen<Name extends Path<SchemaAsserts<T>>>(
          props: Omit<FormItemLumenProps<T, Name>, 'control'>,
        ) {
          return <FormItemLumenComponent {...props} />
        }
      }

      return function UseFormItem<Name extends Path<SchemaAsserts<T>>>(props: Omit<FormItemProps<T, Name>, 'error'>) {
        return <FormItemDecoratedComponent {...props} />
      }
    }, [designSystem]) as FormItemComponent<T, D>,
  }
}

export const useFieldArray = useFieldArrayForm

export const useWatch = useWatchForm

// Helper components

function FormItemDecoratedComponent<T extends SupportedSchema, Name extends Path<SchemaAsserts<T>>>({
  name,
  render,
  control: formControl,
  ...formItemProps
}: Omit<FormItemProps<T, Name>, 'error'>) {
  const { control: contextControl, setValue } = useFormContextForm()
  const control = (formControl || contextControl) as Control<SchemaAsserts<T>>
  const autoCompletedFields = useWatch<SchemaAsserts<T>>({
    control,
    name: 'autoCompletedFields' as Path<SchemaAsserts<T>>,
  })
  const prevValueRef = useRef<UnpackNestedValue<PathValue<SchemaAsserts<T>, Name>>>()

  const isAutoCompleted = Boolean(autoCompletedFields?.[name])

  return (
    <Controller<SchemaAsserts<T>, Name>
      control={control as any}
      name={name}
      render={(props) => {
        // @todo remove any in the following two lines
        const fieldError: any = props.fieldState.error
        const error = (fieldError?.id || fieldError?.name || fieldError) as any | undefined | string

        if (
          isAutoCompleted &&
          prevValueRef.current !== undefined &&
          !isEqual(prevValueRef.current, props.field.value)
        ) {
          setValue('autoCompletedFields', { ...autoCompletedFields, [name]: false })
        }

        prevValueRef.current = props.field.value

        return (
          <FormItemDS {...formItemProps} name={name} error={error?.message}>
            {({ id }: FormItemDecoratedDS) =>
              render({
                ...props,
                // 'disconnect' is set to true to easily integrate legacy selectors which are wrapped redundantly with Controllers
                field: { ...props.field, autoCompleted: isAutoCompleted, id, error: !!error, disconnect: true },
              })
            }
          </FormItemDS>
        )
      }}
    />
  )
}

// Lumen inputs render their own label and error, so unlike the Billy variant nothing is wrapped around the field.
function FormItemLumenComponent<T extends SupportedSchema, Name extends Path<SchemaAsserts<T>>>({
  control: formControl,
  name,
  render,
}: FormItemLumenProps<T, Name>) {
  const formContext = useFormContextForm<SchemaAsserts<T>>()
  const control = formControl ?? formContext.control

  return (
    <Controller<SchemaAsserts<T>, Name>
      control={control}
      name={name}
      render={({ field, fieldState, formState }) =>
        render({ error: fieldState.error?.message, field, fieldState, formState })
      }
    />
  )
}

// Helper types

type FormItemLumenProps<T extends SupportedSchema, Name extends Path<SchemaAsserts<T>>> = {
  control?: Control<SchemaAsserts<T>>
  name: Name
  render: ({
    error,
    field,
    fieldState,
    formState,
  }: {
    error?: string
    field: ControllerRenderProps<SchemaAsserts<T>, Name>
    fieldState: ControllerFieldState
    formState: UseFormStateReturn<SchemaAsserts<T>>
  }) => ReactElement
}

export type FormItemProps<T extends SupportedSchema, Name extends Path<SchemaAsserts<T>>> = Omit<
  FormItemPropsDS,
  'children' | 'name'
> & {
  children?: ReactNode
  name: Name
  control?: Control<SchemaAsserts<T>>
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<SchemaAsserts<T>, Name> & {
      autoCompleted: boolean
      disconnect: boolean
      error: boolean
      id: string
    }
    fieldState: ControllerFieldState
    formState: UseFormStateReturn<SchemaAsserts<T>>
  }) => ReactElement
}
